import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { exportStoryImage } from "./exportStoryImage";
import {
  Bookmark, Download, EyeOff, Heart, Link2, Loader2, MoreVertical,
  AlertCircle, CopyCheck, Share2, ThumbsDown, ThumbsUp,
} from "lucide-react";
import { toast } from "sonner";
import { useBookmarks } from "./BookmarkContext";
import { apiFetch } from "../../utils/api";
import StoryShareCard from "./StoryShareCard";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

/* ─────────────────────────────────────────────────────────
   StoryFeedbackBar
   • Love       — independent toggle  (localStorage dnh:love:{id})
   • Up / Down  — mutually exclusive  (localStorage dnh:react:{id})
   • Bookmark   — BookmarkContext (localStorage + backend sync)
   • Share      — accessible dialog: social links · Copy · Save image
   ───────────────────────────────────────────────────────── */

const lsReactKey = (id) => `dnh:react:${id}`;
const lsLoveKey  = (id) => `dnh:love:${id}`;
const readReaction = (id) => { try { return localStorage.getItem(lsReactKey(id)) || null; } catch { return null; } };
const readLoved    = (id) => { try { return localStorage.getItem(lsLoveKey(id)) === "1"; } catch { return false; } };

/* ── Brand SVG icons ── */
const XIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.91-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);
const WhatsAppIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);
const FacebookIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);
const InstagramIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
  </svg>
);
const LinkedInIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);
const TelegramIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.34-.012.678.128.894.396.34.345.34.9-.127 1.337L15.49 10.9c-.173.165-.346.33-.52.494l-3.932 3.727c-.217.205-.45.395-.717.528-.52.258-1.158.25-1.63-.078-.288-.2-.497-.507-.553-.865l-.654-4.272c-.07-.46.07-.944.382-1.286.312-.342.745-.515 1.19-.486 1.16.073 2.3.52 3.248 1.245l4.77-3.437c.197-.143.44-.178.663-.094z" />
  </svg>
);

/* ── Small pressable icon button ── */
const FbBtn = ({ icon: Icon, active, activeClass, label, onClick, testId, filled = false, disabled = false, light = false, ...props }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    aria-pressed={active}
    disabled={disabled}
    onClick={onClick}
    data-testid={testId}
    {...props}
    className={`inline-flex items-center justify-center h-10 w-9 shrink-0 rounded-[12px] transition-colors disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--crimson)] ${
      active
        ? `${activeClass} bg-transparent`
        : light ? "text-white/85 hover:text-white hover:bg-white/10" : "text-[var(--muted)] hover:text-[var(--ink-2)] hover:bg-[var(--surface-2)]"
    }`}
  >
    <Icon size={17} strokeWidth={1.75} fill={active && filled ? "currentColor" : "none"} />
  </button>
);

/* ── Social/utility button in share grid ── */
const ShareGridBtn = ({ onClick, iconEl, label, iconBg, iconFg = "#fff", testId, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className="flex flex-col items-center gap-1.5 pt-2.5 pb-2 px-0.5 rounded-[8px] hover:bg-[var(--surface-2)] transition-colors disabled:opacity-50 select-none"
  >
    <span
      className="inline-flex items-center justify-center w-9 h-9 rounded-full shrink-0"
      style={{ background: iconBg, color: iconFg }}
    >
      {iconEl}
    </span>
    <span className="font-mono text-[7.5px] tracking-[0.04em] uppercase text-[var(--ink-2)] leading-none text-center w-full">
      {label}
    </span>
  </button>
);

/* ═══════════════════════════════════════════════════════════
   SharePopover — social share + image capture
   ═══════════════════════════════════════════════════════════ */
const SharePopover = ({ story, onClose, onCloseFocus }) => {
  const id      = story?.id || story?._id || story?.story_id || "";
  const cardRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  /* Shared content */
  // Use the backend OG-proxy URL so LinkedIn/WhatsApp/Twitter see real meta tags.
  // The backend serves HTML with OG tags and immediately redirects browsers to /briefs/{id}.
  const BASE = process.env.REACT_APP_BACKEND_URL || "";
  const briefUrl   = id ? `${BASE}/api/brief/open/${id}` : window.location.href;
  const title      = story?.headline || story?.title || "Daily News Highlights";
  const rawDesc    = story?.summary || story?.description || "";
  /* ~120 char excerpt for use in text-limited platforms */
  const excerpt    = rawDesc.length > 120 ? rawDesc.slice(0, 117) + "…" : rawDesc;

  /* ── Platform share URLs with title + description ── */
  const shareToX = () => {
    /* Twitter: title + excerpt + URL (within ~280 chars) */
    const text = `${title.slice(0, 100)}\n\n${excerpt}\n`;
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(briefUrl)}`,
      "_blank", "noopener,noreferrer"
    );
    onClose();
  };

  const shareToWhatsApp = () => {
    /* WhatsApp: bold title + description + link */
    const text = `*${title}*\n\n${rawDesc ? rawDesc.slice(0, 300) + "\n\n" : ""}${briefUrl}`;
    window.open(
      `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`,
      "_blank", "noopener,noreferrer"
    );
    onClose();
  };

  const shareToFacebook = () => {
    /* Facebook sharer uses OG meta for image + title + description */
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(briefUrl)}`,
      "_blank", "noopener,noreferrer"
    );
    onClose();
  };

  const shareToInstagram = async () => {
    /* Instagram has no web share URL — save the card image so the user
       can post it directly, then open Instagram */
    if (cardRef.current) {
      try {
        setSaving(true);
        const { dataUrl, missingPhoto } = await exportStoryImage(cardRef.current);
        const link    = document.createElement("a");
        link.download = `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}.png`;
        link.href     = dataUrl;
        link.click();
        if(missingPhoto) toast.warning("Saved without the publisher photo: the source blocked access.");
        else toast.success("Image saved — open Instagram to post it");
      } catch (e) { toast.error("Couldn't save the image. Please try again."); return; }
      finally { setSaving(false); }
    } else {
      toast.error("Card not ready"); return;
    }
    window.open("https://www.instagram.com", "_blank", "noopener,noreferrer");
    onClose();
  };

  const shareToLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(briefUrl)}`,
      "_blank", "noopener,noreferrer"
    );
    onClose();
  };

  const shareToTelegram = () => {
    /* Telegram: title + URL */
    window.open(
      `https://t.me/share/url?url=${encodeURIComponent(briefUrl)}&text=${encodeURIComponent(title)}`,
      "_blank", "noopener,noreferrer"
    );
    onClose();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(briefUrl);
      toast.success("Link copied");
      onClose();
    } catch {
      setCopyFailed(true);
    }
  };

  const saveImage = async () => {
    if (!cardRef.current) { toast.error("Card not ready"); return; }
    setSaving(true);
    try {
      const { dataUrl, missingPhoto } = await exportStoryImage(cardRef.current);
      const link    = document.createElement("a");
      const slug    = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40);
      link.download = `${slug}.png`;
      link.href     = dataUrl;
      link.click();
      if(missingPhoto) toast.warning("Saved without the publisher photo: the source blocked access.");
      else toast.success("Image saved");
      onClose();
    } catch (err) {
      toast.error("Couldn't save image");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Portal: position:fixed keeps it out of layout flow → no flicker on mount */}
      {typeof document !== "undefined" &&
        createPortal(<StoryShareCard ref={cardRef} story={story} />, document.body)
      }
      <DialogContent
        onCloseAutoFocus={onCloseFocus}
        className="max-w-[calc(100vw-2rem)] sm:max-w-sm bg-[var(--paper)] text-[var(--ink)] border border-[var(--hairline-strong)] rounded-[12px] p-4 gap-3 max-h-[85dvh] overflow-y-auto"
        data-testid={`share-popover-${id}`}
      >
        {/* Header */}
        <div className="px-4 pt-3.5 pb-2.5 border-b border-[var(--hairline)]">
          <DialogTitle data-testid={`share-title-${id}`} className="text-lg pr-7">Share story</DialogTitle>
          <DialogDescription data-testid={`share-description-${id}`} className="mt-2 text-xs line-clamp-2">{title}</DialogDescription>
        </div>

        {/* ── Social platforms — 3×2 grid: X FB IG / LN WA TG ── */}
        <div className="px-2.5 pt-3 pb-1">
          <p className="font-mono text-[8px] tracking-[0.1em] uppercase text-[var(--muted)] mb-2 px-0.5">Social</p>
          <div className="grid grid-cols-3 gap-0">
            <ShareGridBtn onClick={shareToX}          iconEl={<XIcon />}         label="X"         iconBg="#000000"  testId={`share-x-${id}`} />
            <ShareGridBtn onClick={shareToFacebook}   iconEl={<FacebookIcon />}  label="Facebook"  iconBg="#1877F2"  testId={`share-facebook-${id}`} />
            <ShareGridBtn onClick={shareToInstagram} disabled={saving} iconEl={<InstagramIcon />} label="Instagram" iconBg="#c13584" testId={`share-instagram-${id}`} />
            <ShareGridBtn onClick={shareToLinkedIn}   iconEl={<LinkedInIcon />}  label="LinkedIn"  iconBg="#0A66C2"  testId={`share-linkedin-${id}`} />
            <ShareGridBtn onClick={shareToWhatsApp}   iconEl={<WhatsAppIcon />}  label="WhatsApp"  iconBg="#25D366"  testId={`share-whatsapp-${id}`} />
            <ShareGridBtn onClick={shareToTelegram}   iconEl={<TelegramIcon />}  label="Telegram"  iconBg="#2AABEE"  testId={`share-telegram-${id}`} />
          </div>
        </div>

        {/* ── Utilities — Copy + Save ── */}
        <div className="px-2.5 pt-1 pb-3">
          <p className="font-mono text-[8px] tracking-[0.1em] uppercase text-[var(--muted)] mb-2 px-0.5">Save</p>
          <div className="grid grid-cols-2 gap-1.5">
            <button
              type="button"
              onClick={copyLink}
              data-testid={`share-copy-link-${id}`}
              className="flex items-center gap-2 h-9 px-3 rounded-[9px] bg-[var(--surface-2)] hover:bg-[var(--hairline)] transition-colors text-[var(--ink-2)] hover:text-[var(--ink)]"
            >
              <Link2 size={13} strokeWidth={1.75} />
              <span className="font-mono text-[9px] tracking-[0.06em] uppercase">Copy link</span>
            </button>
            <button
              type="button"
              onClick={saveImage}
              disabled={saving}
              data-testid={`share-save-image-${id}`}
              className="flex items-center gap-2 h-9 px-3 rounded-[9px] bg-[var(--surface-2)] hover:bg-[var(--hairline)] transition-colors text-[var(--ink-2)] hover:text-[var(--ink)] disabled:opacity-50"
            >
              {saving
                ? <Loader2 size={13} strokeWidth={1.75} className="animate-spin" />
                : <Download size={13} strokeWidth={1.75} />
              }
              <span className="font-mono text-[9px] tracking-[0.06em] uppercase">
                {saving ? "Saving…" : "Save image"}
              </span>
            </button>
          </div>
        </div>
        {copyFailed && <div data-testid={`share-copy-fallback-${id}`}><p role="status" data-testid={`share-copy-error-${id}`} className="text-xs mb-2">Clipboard unavailable. Select and copy this link:</p><input readOnly value={briefUrl} aria-label="Story link" onFocus={event => event.target.select()} data-testid={`share-link-input-${id}`} className="w-full min-w-0 border border-[var(--hairline)] p-3 text-xs bg-[var(--paper)]" /></div>}
      </DialogContent>
    </>
  );
};

/* ══════════════════════════════════════════════════════════
   AccuracyDialog — opened from the ⋮ menu when searchFeedback is present
   ══════════════════════════════════════════════════════════ */
const AccuracyDialog = ({ open, onOpenChange, onSubmit, prefix }) => {
  const [note, setNote] = useState("");
  const submit = () => { onSubmit(note); setNote(""); onOpenChange(false); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-[var(--paper)] text-[var(--ink)] border border-[var(--hairline-strong)] rounded-[12px] p-5">
        <DialogTitle className="text-base">Report accuracy issue</DialogTitle>
        <DialogDescription className="text-xs text-[var(--muted)]">
          Briefly describe the concern (optional, up to 500 characters).
        </DialogDescription>
        <textarea
          data-testid={`${prefix}-accuracy-note`}
          value={note}
          onChange={e => setNote(e.target.value.slice(0, 500))}
          placeholder="e.g. wrong date, misattributed quote…"
          rows={3}
          className="w-full text-sm border border-[var(--hairline)] rounded-lg px-3 py-2 bg-transparent resize-none focus:outline-none focus:border-[var(--ink)]"
        />
        <div className="flex gap-3 justify-end">
          <button data-testid={`${prefix}-accuracy-cancel`} onClick={() => { setNote(""); onOpenChange(false); }} className="text-xs text-[var(--muted)] hover:text-[var(--ink)] transition-colors min-h-9 px-2">Cancel</button>
          <button data-testid={`${prefix}-accuracy-submit`} onClick={submit} className="text-xs bg-[var(--ink)] text-[var(--paper)] px-4 py-2 rounded-lg hover:bg-[var(--crimson)] transition-colors">Submit report</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ══════════════════════════════════════════════════════════
   Main exported component
   ══════════════════════════════════════════════════════════ */
export const StoryFeedbackBar = ({
  story,
  showBookmark = false,
  light = false,
  /* searchFeedback: { query, itemId, prefix, sendFeedback } — injects
     "Not relevant" + "Accuracy issue" into the ⋮ menu */
  searchFeedback = null,
}) => {
  const id = story?.id || story?._id || "";

  /* Love — fully independent */
  const [guestLoved, setLoved] = useState(() => id ? readLoved(id) : false);
  /* Up / Down — mutually exclusive */
  const [guestReaction, setReaction] = useState(() => id ? readReaction(id) : null);

  /* Bookmark */
  const { isBookmarked, toggleBookmark, feedback, saveFeedback, signedIn, syncing } = useBookmarks();
  const loved = signedIn ? !!feedback[id]?.loved : guestLoved;
  const reaction = signedIn ? feedback[id]?.reaction || null : guestReaction;
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const bookmarked = isBookmarked(id);

  /* Share popover */
  const [shareOpen, setShareOpen] = useState(false);
  const shareRef = useRef(null);

  /* ⋮ menu search feedback state */
  const [notRelevantSent, setNotRelevantSent] = useState(false);
  const [accuracySent, setAccuracySent] = useState(false);
  const [accuracyOpen, setAccuracyOpen] = useState(false);

  /* Duplicate report */
  const [reported, setReported] = useState(false);

  React.useEffect(() => {
    const syncGuest = () => { setLoved(readLoved(id)); setReaction(readReaction(id)); };
    syncGuest();
    window.addEventListener("storage", syncGuest);
    window.addEventListener("dnh:feedback-changed", syncGuest);
    return () => { window.removeEventListener("storage", syncGuest); window.removeEventListener("dnh:feedback-changed", syncGuest); };
  }, [id]);

  if (!id) return null;

  /* ── Handlers ── */
  const handleLove = async (e) => {
    e.stopPropagation();
    if (feedbackBusy) return;
    const next = !loved;
    if (signedIn) {
      setFeedbackBusy(true);
      try { await saveFeedback(id, { loved: next, reaction }); } catch { toast.error("Feedback could not be saved"); } finally { setFeedbackBusy(false); }
      return;
    }
    setLoved(next);
    try { next ? localStorage.setItem(lsLoveKey(id), "1") : localStorage.removeItem(lsLoveKey(id)); } catch {}
    window.dispatchEvent(new Event("dnh:feedback-changed"));
  };

  const handleReact = async (type, e) => {
    e.stopPropagation();
    if (feedbackBusy) return;
    const next = reaction === type ? null : type;
    if (signedIn) {
      setFeedbackBusy(true);
      try { await saveFeedback(id, { loved, reaction: next }); } catch { toast.error("Feedback could not be saved"); } finally { setFeedbackBusy(false); }
      return;
    }
    setReaction(next);
    try { next ? localStorage.setItem(lsReactKey(id), next) : localStorage.removeItem(lsReactKey(id)); } catch {}
    window.dispatchEvent(new Event("dnh:feedback-changed"));
  };

  const handleBookmark = async () => {
    if (!signedIn) {
      toast.info("Sign in to save stories to your Reading List");
      return;
    }
    try {
      const saved = await toggleBookmark(id);
      toast.success(saved ? "Saved to your Reading List" : "Removed from Reading List");
    } catch { /* Bookmark context displays the actual failure. */ }
  };

  const handleDuplicate = () => {
    setReported(true);
    toast.success("Reported as duplicate — thank you");
  };

  const handleNotRelevant = async () => {
    if (!searchFeedback) return;
    if (!signedIn) { toast.info("Sign in to provide feedback"); return; }
    try {
      await apiFetch("/api/search/feedback", {
        method: "POST",
        body: JSON.stringify({ query: searchFeedback.query, story_id: searchFeedback.itemId, kind: "not_relevant", note: null }),
      });
      setNotRelevantSent(true);
      toast.success("Marked as not relevant");
    } catch { toast.error("Could not send feedback"); }
  };

  const handleAccuracySubmit = async (note) => {
    if (!searchFeedback) return;
    if (!signedIn) { toast.info("Sign in to provide feedback"); return; }
    try {
      await apiFetch("/api/search/feedback", {
        method: "POST",
        body: JSON.stringify({ query: searchFeedback.query, story_id: searchFeedback.itemId, kind: "accuracy", note: note || null }),
      });
      setAccuracySent(true);
      toast.success("Accuracy report sent — thank you");
    } catch { toast.error("Could not send report"); }
  };

  return (
    <>
      <div
        className={`flex items-center justify-between pt-1 border-t ${light ? "border-white/25" : "border-[var(--hairline)]"}`}
        data-testid={`story-feedback-bar-${id}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left: Love + Up/Down reactions */}
        <div className="flex items-center gap-0.5">
          <FbBtn disabled={feedbackBusy || syncing} light={light} icon={Heart} active={loved} activeClass={light ? "text-rose-300" : "text-rose-600"} filled label="Love" onClick={handleLove} testId={`feedback-love-${id}`} />
          <FbBtn disabled={feedbackBusy || syncing} light={light} icon={ThumbsUp} active={reaction === "up"} activeClass={light ? "text-emerald-300" : "text-emerald-700"} filled label="Thumbs up" onClick={(e) => handleReact("up", e)} testId={`feedback-thumbs-up-${id}`} />
          <FbBtn disabled={feedbackBusy || syncing} light={light} icon={ThumbsDown} active={reaction === "down"} activeClass={light ? "text-amber-300" : "text-amber-700"} filled label="Thumbs down" onClick={(e) => handleReact("down", e)} testId={`feedback-thumbs-down-${id}`} />
        </div>

        {/* Right: Share + ⋮ vertical menu */}
        <div className="flex items-center gap-0.5">
          {/* Share */}
          <div className="relative" ref={shareRef}>
            <FbBtn
              light={light}
              aria-haspopup="dialog" aria-expanded={shareOpen}
              icon={Share2} active={shareOpen} activeClass="text-[var(--ink-2)] bg-[var(--surface-2)]"
              label="Share story"
              onClick={(e) => { e.stopPropagation(); setShareOpen((v) => !v); }}
              testId={`feedback-share-${id}`}
            />
            <Dialog open={shareOpen} onOpenChange={setShareOpen}>
              {shareOpen && <SharePopover story={story} onClose={() => setShareOpen(false)} onCloseFocus={event => { event.preventDefault(); shareRef.current?.querySelector("button")?.focus(); }} />}
            </Dialog>
          </div>

          {/* ⋮ Vertical dots menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid={`feedback-more-${id}`}
                aria-label="More actions"
                onClick={(e) => e.stopPropagation()}
                className={`inline-flex items-center justify-center h-10 w-9 shrink-0 rounded-[12px] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--crimson)] ${light ? "text-white/85 hover:text-white hover:bg-white/10" : "text-[var(--muted)] hover:text-[var(--ink-2)] hover:bg-[var(--surface-2)]"}`}
              >
                <MoreVertical size={17} strokeWidth={1.75} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={4}
              className="bg-[var(--paper)] border border-[var(--hairline-strong)] rounded-[10px] shadow-lg min-w-[170px] p-1"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Bookmark */}
              {showBookmark && (
                <DropdownMenuItem
                  data-testid={`feedback-bookmark-${id}`}
                  disabled={syncing}
                  onSelect={handleBookmark}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-[6px] text-[13px] cursor-pointer"
                >
                  <Bookmark size={14} strokeWidth={1.75} className={bookmarked ? "text-[var(--ink)]" : "text-[var(--muted)]"} fill={bookmarked ? "currentColor" : "none"} />
                  <span>{bookmarked ? "Remove from saved" : "Save story"}</span>
                </DropdownMenuItem>
              )}

              {/* Duplicate */}
              <DropdownMenuItem
                data-testid={`feedback-report-duplicate-${id}`}
                disabled={reported}
                onSelect={handleDuplicate}
                className="flex items-center gap-2.5 px-3 py-2 rounded-[6px] text-[13px] cursor-pointer"
              >
                <CopyCheck size={14} strokeWidth={1.75} className={reported ? "text-[var(--crimson)]" : "text-[var(--muted)]"} />
                <span>{reported ? "Reported" : "Mark as duplicate"}</span>
              </DropdownMenuItem>

              {/* Search feedback items — only when searchFeedback is provided */}
              {searchFeedback && (
                <>
                  <DropdownMenuSeparator className="my-1 bg-[var(--hairline)]" />

                  {/* Not relevant */}
                  <DropdownMenuItem
                    data-testid={`${searchFeedback.prefix}-not_relevant-button`}
                    disabled={notRelevantSent}
                    onSelect={handleNotRelevant}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-[6px] text-[13px] cursor-pointer text-[var(--muted)]"
                  >
                    <EyeOff size={14} strokeWidth={1.75} />
                    <span>{notRelevantSent ? "Marked as not relevant" : "Not relevant"}</span>
                  </DropdownMenuItem>

                  {/* Accuracy issue */}
                  <DropdownMenuItem
                    data-testid={`${searchFeedback.prefix}-accuracy-button`}
                    disabled={accuracySent}
                    onSelect={() => { if (!accuracySent) setAccuracyOpen(true); }}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-[6px] text-[13px] cursor-pointer text-[var(--muted)]"
                  >
                    <AlertCircle size={14} strokeWidth={1.75} />
                    <span>{accuracySent ? "Accuracy report sent" : "Accuracy issue"}</span>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Accuracy issue dialog — rendered outside the dropdown to avoid portal nesting issues */}
      {searchFeedback && (
        <AccuracyDialog
          open={accuracyOpen}
          onOpenChange={setAccuracyOpen}
          onSubmit={handleAccuracySubmit}
          prefix={searchFeedback.prefix}
        />
      )}
    </>
  );
};

export default StoryFeedbackBar;